import React, { useEffect, useState } from "react";
import Card from "components/card";

import { generaleTableApi, generaleTableXcelApi } from "api/dashboard";
import { getAllCategoriesApi } from "api/category";
import { getAllTcpsApi } from "api/filtersData";
import { Spinner } from "@material-tailwind/react";
import PieChart from "components/charts/PieChart";
import BarChart from "components/charts/BarChart";
import RadarChart from "components/charts/RadarChart";
import API_URL from "config";

const GlobaleTable = ({ accessList = {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingXcel, setIsLoadingXcel] = useState(false);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [tableData, setTableData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [tcpData, setTcpData] = useState([]);
  const [selectedTcp, setSelectedTcp] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // radar-specific filters (Comparaison TCP)
  const [radarCategory, setRadarCategory] = useState("");
  const [radarProduct, setRadarProduct] = useState("");

  const getInitialData = () => {
    try {
      setIsLoading(true);
      if (fromDate && toDate) {
        generaleTableApi(fromDate, toDate)
        .then((generaleTableRes) => {
          setTableData(generaleTableRes);
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
      } else {
        generaleTableApi(null, null)
        .then((generaleTableRes) => {
          setTableData(generaleTableRes);
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
      }

      getAllCategoriesApi()
        .then(allCategories => {
          setCategoriesData(allCategories || []);
        })
        .catch(err => {
          console.log(err);
        })

      getAllTcpsApi()
        .then(allTcps => {
          setTcpData(allTcps || []);
        })
        .catch(err => {
          console.log(err);
        })
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterData = () => {
    try {
      getInitialData();
    }
    catch (err) {
      
    }
  }

  const handleDownloadXcel = async () => {
    if (!accessList?.pdf_excel) return;
    try {
      setIsLoadingXcel(true);
      if (fromDate && toDate) {
        const generalXcelTableRes = await generaleTableXcelApi(
          fromDate,
          toDate
        );

        window.open(`${API_URL}/${generalXcelTableRes}`, "_blank");
      } else {
        const generalXcelTableRes = await generaleTableXcelApi(null, null);

        window.open(`${API_URL}/${generalXcelTableRes}`, "_blank");
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoadingXcel(false);
    }
  };

  // Format number with space as thousand separators
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-EN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Compact number for chart labels (e.g. 1.2M, 45k) to avoid clutter
  const compactNumber = (value) => {
    const v = parseFloat(value) || 0;
    const abs = Math.abs(v);
    if (abs >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
    if (abs >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, "")}k`;
    return `${Math.round(v)}`;
  };

  // Build aggregates for charts from generale table data
  const getCategoryPieData = () => {
    if (!tableData?.data) return { series: [], labels: [] };

    const categoryTotals = new Map();
    Object.entries(tableData.data)
      .filter(([tcp]) => !selectedTcp || tcp === selectedTcp)
      .forEach(([tcp, products]) => {
        Object.entries(products).forEach(([productName, entries]) => {
          const category = entries?.[0]?.Category || 'Autre';
          const amount = parseFloat(entries[entries.length - 1]?.sumOfAmount || 0);
          categoryTotals.set(category, (categoryTotals.get(category) || 0) + amount);
        });
      });

    const labels = Array.from(categoryTotals.keys());
    const series = labels.map((l) => categoryTotals.get(l));
    return { labels, series };
  };

  const getTopProductsBarData = () => {
    if (!tableData?.data) return { series: [], categories: [] };
    const totals = [];
    Object.entries(tableData.data)
      .filter(([tcp]) => !selectedTcp || tcp === selectedTcp)
      .forEach(([tcp, products]) => {
        Object.entries(products).forEach(([productName, entries]) => {
          const category = entries?.[0]?.Category || 'Autre';
          if (selectedCategory && category !== selectedCategory) return;
          const amount = parseFloat(entries[entries.length - 1]?.sumOfAmount || 0);
          totals.push({ name: productName, amount });
        });
      });
    const top10 = totals.sort((a, b) => b.amount - a.amount).slice(0, 10);
    return {
      series: [{ name: 'Montant HT', data: top10.map((t) => t.amount) }],
      categories: top10.map((t) => t.name),
    };
  };

  // Normalize category name for matching (remove spaces, convert to lowercase)
  const normalizeCategoryName = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/\s+/g, '').trim();
  };

  // Get colors for pie chart based on category colors
  const getCategoryColors = () => {
    const { labels } = getCategoryPieData();
    const defaultColors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', 
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];
    
    // Create a map of normalized category name to color
    const categoryColorMap = {};
    categoriesData.forEach(category => {
      if (category.Name && category.Color) {
        const normalizedName = normalizeCategoryName(category.Name);
        categoryColorMap[normalizedName] = category.Color;
      }
    });
    
    // Map each label to its category color, or use default color if not found
    return labels.map((label, index) => {
      const normalizedLabel = normalizeCategoryName(label);
      return categoryColorMap[normalizedLabel] || defaultColors[index % defaultColors.length];
    });
  };

  const getPieOptions = () => ({
    chart: {
      type: 'pie',
      height: 380,
      width: '100%',
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const clickedLabel = config.w.globals.labels[config.dataPointIndex];
          setSelectedCategory(prev => prev === clickedLabel ? "" : clickedLabel);
        },
      },
    },
    labels: getCategoryPieData().labels,
    colors: getCategoryColors(),
    legend: { position: 'bottom' },
    tooltip: {
      enabled: true,
      theme: 'dark',
      style: {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        background: '#000000'
      },
      y: {
        formatter: function (val) {
          return formatNumber(val) + " DH";
        }
      },
      fillSeriesColor: false,
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        const categoryName = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];
        const total = series.reduce((a, b) => a + b, 0);
        const percentage = ((value / total) * 100).toFixed(1);
        
        return '<div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 15px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">' +
          '<div style="font-weight: 600; margin-bottom: 5px;">' + categoryName + '</div>' +
          '<div style="font-size: 20px; font-weight: bold">' + formatNumber(value) + ' DH</div>' +
          '<div style="font-size: 18px; color: #9CA3AF; margin-top: 3px; font-weight: bold">' + percentage + '%</div>' +
          '</div>';
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const amount = formatNumber(opts.w.config.series[opts.seriesIndex]);
        return `${amount} DH\n(${val.toFixed(1)}%)`;
      },
      style: {
        fontSize: '20px',
        fontWeight: 500,
        colors: ['#000000']
      },
      background: {
        enabled: true,
        padding: 6,
        borderRadius: 8,
        borderWidth: 0,
        opacity: 0.92,
        dropShadow: { enabled: false }
      }
    },
  });

  const getBarOptions = () => {
    const { categories } = getTopProductsBarData();
    return {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
      xaxis: { categories, labels: { formatter: (v) => formatNumber(v) } },
      yaxis: { labels: { style: { fontSize: '12px' } } },
      dataLabels: { 
        enabled: true,
        formatter: (val, opts) => {
          const name = categories[opts.dataPointIndex] || '';
          // return `${formatNumber(val)} DH\n${name}`;
        },
        offsetX: 8,
        style: { 
          fontSize: '12px',
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false }
        },
      },
      tooltip: { 
        y: { formatter: (v) => `${formatNumber(v)} DH` },
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const name = categories[dataPointIndex] || '';
          const value = series[seriesIndex][dataPointIndex];
          return `
            <div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 12px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">
              <div style="font-weight: 700; margin-bottom: 4px;">${formatNumber(value)} DH</div>
              <div style="font-size: 12px; color: #d1d5db;">${name}</div>
            </div>
          `;
        }
      },
      colors: ['#236571'],
      grid: { strokeDashArray: 4 },
    };
  };

  // Distinct categories present in the data (for the radar category filter)
  const getRadarCategoryOptions = () => {
    if (!tableData?.data) return [];
    const set = new Set();
    Object.values(tableData.data).forEach((products) => {
      Object.values(products).forEach((entries) => {
        set.add(entries?.[0]?.Category || 'Autre');
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  // Distinct products present in the data, optionally limited to the radar category
  const getRadarProductOptions = () => {
    if (!tableData?.data) return [];
    const set = new Set();
    Object.values(tableData.data).forEach((products) => {
      Object.entries(products).forEach(([name, entries]) => {
        const cat = entries?.[0]?.Category || 'Autre';
        if (radarCategory && cat !== radarCategory) return;
        set.add(name);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  // Radar: compare Quantité vs Montant HT across top TCPs (by amount)
  const getTopTcpRadarData = () => {
    if (!tableData?.data) return { categories: [], qtySeries: [], amtSeries: [] };

    // active TCP names (tcpData comes from the Tcps filter: Status true, not deleted)
    const activeTcpNames = new Set(tcpData.map((el) => el.Name));

    const aggregates = Object.entries(tableData.data)
      .filter(([tcp]) => activeTcpNames.has(tcp))
      .map(([tcp, products]) => {
      // Sum across products for this TCP, honoring the radar category/product filters
      let qty = 0; let amt = 0;
      Object.entries(products).forEach(([name, entries]) => {
        const cat = entries?.[0]?.Category || 'Autre';
        if (radarCategory && cat !== radarCategory) return;
        if (radarProduct && name !== radarProduct) return;
        qty += parseFloat(entries[entries.length - 1]?.sumOfQuantity || 0);
        amt += parseFloat(entries[entries.length - 1]?.sumOfAmount || 0);
      });
      return { tcp, qty, amt };
    }).filter((t) => t.qty > 0 || t.amt > 0);

    const top = aggregates.sort((a,b) => b.amt - a.amt).slice(0, 10);
    const categories = top.map(t => t.tcp);

    // Normalize values 0-100 so the two different scales (Quantité vs Montant HT)
    // share one radar axis. Real values are kept for the labels/tooltip.
    const maxQty = Math.max(1, ...top.map(t => t.qty));
    const maxAmt = Math.max(1, ...top.map(t => t.amt));
    const qtySeries = top.map(t => Math.round((t.qty / maxQty) * 100));
    const amtSeries = top.map(t => Math.round((t.amt / maxAmt) * 100));

    const qtyRaw = top.map(t => t.qty);
    const amtRaw = top.map(t => t.amt);

    return { categories, qtySeries, amtSeries, qtyRaw, amtRaw };
  };

  const getRadarOptions = (categories = [], qtyRaw = [], amtRaw = []) => {
    // map a plotted (normalized) point back to its real value
    const rawAt = (seriesIndex, dataPointIndex) =>
      seriesIndex === 0 ? qtyRaw[dataPointIndex] : amtRaw[dataPointIndex];

    return {
      chart: {
        type: 'radar',
        toolbar: { show: false },
        width: '100%',
        height: 600
      },
      xaxis: { categories },
      dataLabels: {
        enabled: true,
        // show the real value, not the normalized 0-100 position
        formatter: (val, opts) =>
          compactNumber(rawAt(opts?.seriesIndex, opts?.dataPointIndex)),
        style: {
          fontSize: '14px',
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false }
        }
      },
      stroke: { width: 3 },
      fill: { opacity: 0.3 },
      legend: {
        position: 'bottom',
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif'
      },
      tooltip: {
        y: {
          // exact real value on hover
          formatter: (val, opts) =>
            formatNumber(rawAt(opts?.seriesIndex, opts?.dataPointIndex)),
        },
      },
      yaxis: {
        show: true,
        // the ring scale is relative (0-100); hide its ticks so the
        // real-value labels aren't mistaken for percentages
        labels: { show: false },
      },
      responsive: [{
        breakpoint: 1200,
        options: {
          chart: {
            height: 500
          }
        }
      }, {
        breakpoint: 768,
        options: {
          chart: {
            height: 450
          }
        }
      }]
    };
  };

  useEffect(() => {
    getInitialData();
  }, []); // fromDate, toDate

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner className="h-12 w-12" />
        </div>
      ) : (
        <Card extra={"w-full pb-10 p-4 h-full"}>
          <header className="relative flex items-center justify-between">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              TABLE GENERALE
            </div>
            {isLoadingXcel ? (
              <div className="flex items-center justify-center">
                <Spinner className="h-12 w-12" />
              </div>
            ) : accessList?.pdf_excel ? (
              <button
                href=""
                className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                onClick={handleDownloadXcel}
              >
                Télécharger excel
              </button>
            ) : null}
          </header>

          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-4">
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par date
              </small>
              <div>
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </div>
            </div>
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par TCP
              </small>
              <select
                className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                value={selectedTcp}
                onChange={(e) => setSelectedTcp(e.target.value)}
              >
                <option value="">Tous</option>
                {tcpData.map((el) => (
                  <option key={el._id} value={el.Name}>{el.Name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-4">
            <button
              href=""
              className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
              onClick={handleFilterData}
            >
              Filtrer
            </button>
          </div>

          {/* Charts for a simple overview */}
          {tableData?.data && (
            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
              <Card extra={"w-full p-4"}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-lg font-bold text-navy-700 dark:text-white">Répartition par Catégorie</div>
                  {selectedCategory && (
                    <button
                      onClick={() => setSelectedCategory("")}
                      className="flex items-center gap-1 rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-400"
                    >
                      {selectedCategory} ✕
                    </button>
                  )}
                </div>
                <PieChart
                  series={getCategoryPieData().series}
                  options={getPieOptions()}
                  width="100%"
                  height={400}
                />
              </Card>
              <Card extra={"w-full p-4"}>
                <div className="text-lg font-bold text-navy-700 dark:text-white mb-2">Top 10 Produits par Montant HT</div>
                <BarChart
                  chartData={getTopProductsBarData().series}
                  chartOptions={getBarOptions()}
                  width="100%"
                  height={420}
                />
              </Card>
            </div>
          )}

          {tableData?.data && (
            <div className="mt-5">
              <Card extra={"w-full p-6"}>
                <div className="text-xl font-bold text-navy-700 dark:text-white mb-4">Comparaison TCP (Radar)</div>

                <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <small className="text-sm font-medium text-gray-600 dark:text-white">
                      Filtrer par catégorie
                    </small>
                    <select
                      className="mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
                      value={radarCategory}
                      onChange={(e) => {
                        setRadarCategory(e.target.value);
                        setRadarProduct(""); // reset product when category changes
                      }}
                    >
                      <option value="">Toutes</option>
                      {getRadarCategoryOptions().map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <small className="text-sm font-medium text-gray-600 dark:text-white">
                      Filtrer par produit
                    </small>
                    <select
                      className="mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
                      value={radarProduct}
                      onChange={(e) => setRadarProduct(e.target.value)}
                    >
                      <option value="">Tous</option>
                      {getRadarProductOptions().map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  {(radarCategory || radarProduct) && (
                    <div className="flex items-end">
                      <button
                        onClick={() => {
                          setRadarCategory("");
                          setRadarProduct("");
                        }}
                        className="linear rounded-[10px] bg-gray-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-gray-400"
                      >
                        Réinitialiser
                      </button>
                    </div>
                  )}
                </div>

                {(() => {
                  const { categories, qtySeries, amtSeries, qtyRaw, amtRaw } =
                    getTopTcpRadarData();
                  const series = [
                    { name: 'Quantité', data: qtySeries },
                    { name: 'Montant HT', data: amtSeries },
                  ];
                  return (
                    <div className="w-full">
                      <RadarChart
                        // Remount when filters/TCP set change so ApexCharts radar
                        // doesn't keep stale axis labels & series.
                        key={`${radarCategory}|${radarProduct}|${categories.join(',')}`}
                        series={series}
                        options={getRadarOptions(categories, qtyRaw, amtRaw)}
                        width="100%"
                        height={650}
                      />
                    </div>
                  );
                })()}
              </Card>
            </div>
          )}

          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">TCP</p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">
                      Produit
                    </p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">
                      Catégorie
                    </p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">
                      Quantité
                    </p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">
                      Montant HT
                    </p>
                  </th>
                </tr>
              </thead>

              <tbody>
                {tableData?.data
                  ? Object.entries(tableData?.data)
                      .filter(([key]) => !selectedTcp || key === selectedTcp)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([key, value]) => {
                      const subQty = 0;
                      const subTotal = 0;

                      const subQtySubTotal = {
                        subQty: 0,
                        subTotal: 0
                      }
                      
                      return (
                        <>
                          {Object.entries(value)
                            .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
                            .filter(([, value2]) => !selectedCategory || (value2[0]?.Category || 'Autre') === selectedCategory)
                            .map(([key2, value2]) => {
                            const category = value2[0].Category;

                            const subSubQty = value2[value2.length - 1].sumOfQuantity
                            const subSubTotal = value2[value2.length - 1].sumOfAmount

                            subQtySubTotal.subQty = value2[value2.length - 1].sumOfOneTcpQuantity
                            subQtySubTotal.subTotal = value2[value2.length - 1].sumOfOneTcpAmount

                            // Object.entries(value).map(([index, product]) => {
                            //   subQty = parseFloat(product[product.length - 1].sumOfOneTcpQuantity)
                            //   subTotal = parseFloat(product[product.length - 1].sumOfOneTcpAmount)
                            //   // product.map((el) => {
                            //   //   subQty += parseFloat(el.Quantity);
                            //   //   subTotal += parseFloat(el.Amount);
                            //   // });
                            // });
                            return (
                              <tr>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {key}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {key2}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {category}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                    {formatNumber(subSubQty.toFixed(2))}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                    {formatNumber(subSubTotal.toFixed(2))}
                                  </p>
                                </td>
                              </tr>
                            );
                          })}

                          <tr className="bg-gray-100 dark:bg-brand-400">
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                {formatNumber(subQtySubTotal.subQty.toFixed(2))}
                              </p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                {formatNumber(subQtySubTotal.subTotal.toFixed(2))}
                              </p>
                            </td>
                          </tr>
                        </>
                      );
                    })
                  : ""}

                <tr className="mt-5 bg-gray-200 dark:bg-brand-400">
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfQuantity?.toFixed(2))}
                    </p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfAmount?.toFixed(2))}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
};

export default GlobaleTable;
